/**
 * §7.29 AMR usage → resistance-emergence feedback loop.
 *
 * Activates the Phase 8.5 Tier-1 AMR catalog batch (acinetobacter-baumannii-mdr,
 * mrsa, candida-auris, gonorrhoeae-amr, clostridioides-difficile) by giving
 * operators a forward-projection surface that ties antibiotic CONSUMPTION
 * (defined daily doses per 1000 inhabitants per day — the ESAC-Net /
 * CDC NARMS / WHO GLASS canonical unit) to RESISTANCE EMERGENCE over a
 * multi-year horizon. Stewardship interventions (reduce consumption %)
 * project the time-lag to resistance reversal.
 *
 * No ESAC-Net / NARMS / GLASS PARTNERSHIP is wired (see the
 * partnership-rationale block below) — this surface uses literature-anchored
 * resistance-elasticity coefficients + operator-tunable consumption inputs.
 * When a partnership lands (Phase 13+), the same surface consumes the live
 * adapter; the model + UI do not change.
 *
 * ## Literature anchors (resistance elasticity per +1 DDD/1000/day, 5-year horizon)
 *
 * The "elasticity" coefficient `e` is the marginal change in resistance %
 * for a sustained +1 DDD/1000/day shift in the relevant antibiotic-class
 * consumption, projected over a 5-year horizon. Sourced from systematic
 * reviews + ecological time-series analyses.
 *
 *   - MRSA vs β-lactam + fluoroquinolone exposure:
 *       Goossens et al. 2005 (Lancet — outpatient antibiotic use vs resistance
 *       across 26 European countries), PMID 15708101: r=0.75 for fluoroquinolone
 *       use vs MRSA prevalence. Adopted e = 0.018 per +1 DDD/1000/day (mid-range
 *       of ecological estimates).
 *   - Gonorrhoea AMR vs macrolide + cephalosporin exposure:
 *       Kirkcaldy et al. 2013 (CID — US GISP) + WHO GASP 2017-21 trends.
 *       Adopted e = 0.024 per +1 DDD/1000/day (macrolide-driven).
 *   - C. difficile incidence vs broad-spectrum antibiotic exposure:
 *       Pakyz et al. 2007 (ICHE — US hospital ecological) + Dingle et al.
 *       2017 (Lancet ID — Oxford fluoroquinolone-restriction natural exp.,
 *       PMID 28461207). FQ restriction → ribotype 027 CDI down 80%+ in 4y;
 *       adopted e = 0.030 per +1 DDD/1000/day.
 *   - Acinetobacter baumannii MDR vs carbapenem exposure:
 *       Lee et al. 2007 + ECDC EARS-Net trend reports. Adopted e = 0.040 per
 *       +1 DDD/1000/day (carbapenem class is the dominant driver).
 *   - Candida auris vs systemic azole exposure:
 *       Lockhart et al. 2017 (CID — global emergence) + CDC C. auris
 *       situation reports. Resistance emergence is dominated by HORIZONTAL
 *       transmission in hospitals, NOT consumption-driven selection;
 *       elasticity is low (e = 0.005) — model surfaces this as a CAVEAT
 *       so operators don't over-weight the stewardship lever here.
 *
 * ## Why no ESAC-Net / NARMS / WHO GLASS partnership (yet)
 *
 * Live consumption-data ingest from any of these three would be the right
 * way to wire this surface to ground truth. We are not yet there:
 *
 *   - ESAC-Net (ECDC): country-aggregate annual ATC/DDD data is published
 *     openly via the ECDC Surveillance Atlas (covered by ECDC's open-data
 *     attribution rules) but the granular hospital-vs-community breakdown
 *     + monthly resolution requires an EU-member-state data-sharing
 *     agreement we don't have.
 *   - CDC NARMS: focuses on resistance in foodborne pathogens (Salmonella /
 *     Campylobacter / E. coli), not directly on the priority Gram-negatives
 *     + Gram-positives the catalog tracks. Adjacent rather than core.
 *   - WHO GLASS: country-submitted data published in annual reports;
 *     bilateral data-use agreements gate machine-readable feeds.
 *
 * The Phase 13 unblock-path is a written partnership-channel ask via the
 * same WHO partner-channel route the BioMax SDK uses for ICD-11 / WHODrug
 * tooling. Until then: literature-anchored coefficients + operator-tunable
 * inputs make this surface usefully indicative without false precision.
 */

import { useMemo, useState } from 'react';
import { pinForBrief } from '../pack/pin-to-brief';

interface AmrTarget {
  pathogenId: string;
  catalogLabel: string;
  /** Antibiotic class whose consumption drives selection pressure here. */
  selectionDriver: string;
  /** Marginal resistance-prevalence change per +1 DDD/1000/day (5-year). */
  elasticity: number;
  /** Baseline resistance prevalence % at the reference consumption level. */
  baselineResistancePct: number;
  /** Whether this organism's resistance is dominantly consumption-driven (vs
   *  transmission-driven). When false, stewardship lever is weakly effective
   *  and the panel surfaces a caveat. */
  consumptionDriven: boolean;
  /** Primary literature anchor (PMID where available). */
  primarySource: string;
}

const AMR_TARGETS: readonly AmrTarget[] = [
  {
    pathogenId: 'mrsa',
    catalogLabel: 'MRSA',
    selectionDriver: 'β-lactam + fluoroquinolone',
    elasticity: 0.018,
    baselineResistancePct: 22,
    consumptionDriven: true,
    primarySource: 'Goossens 2005 (PMID 15708101)',
  },
  {
    pathogenId: 'gonorrhoeae-amr',
    catalogLabel: 'Gonorrhoea AMR',
    selectionDriver: 'macrolide + cephalosporin',
    elasticity: 0.024,
    baselineResistancePct: 11,
    consumptionDriven: true,
    primarySource: 'Kirkcaldy 2013 + WHO GASP 2017-21',
  },
  {
    pathogenId: 'clostridioides-difficile',
    catalogLabel: 'C. difficile',
    selectionDriver: 'broad-spectrum (esp. fluoroquinolone)',
    elasticity: 0.030,
    baselineResistancePct: 0,  // CDI is incidence-rate driven not resistance-driven; surface as cases/100k
    consumptionDriven: true,
    primarySource: 'Dingle 2017 (PMID 28461207)',
  },
  {
    pathogenId: 'acinetobacter-baumannii-mdr',
    catalogLabel: 'A. baumannii MDR',
    selectionDriver: 'carbapenem',
    elasticity: 0.040,
    baselineResistancePct: 45,
    consumptionDriven: true,
    primarySource: 'Lee 2007 + ECDC EARS-Net',
  },
  {
    pathogenId: 'candida-auris',
    catalogLabel: 'C. auris',
    selectionDriver: 'systemic azole',
    elasticity: 0.005,
    baselineResistancePct: 90,  // C. auris is intrinsically multi-azole-resistant
    consumptionDriven: false,
    primarySource: 'Lockhart 2017 (CID) + CDC sitreps',
  },
];

interface StewardshipScenario {
  id: string;
  label: string;
  /** % reduction in consumption vs the baseline. */
  consumptionDeltaPct: number;
  description: string;
}

const STEWARDSHIP_SCENARIOS: readonly StewardshipScenario[] = [
  { id: 'status-quo',  label: 'Status quo',                        consumptionDeltaPct: 0,
    description: 'No change in prescribing patterns — baseline trajectory.' },
  { id: 'modest',      label: 'Modest stewardship (–10%)',         consumptionDeltaPct: -10,
    description: 'Audit + feedback + diagnostic stewardship in tertiary hospitals (CDC Core Elements compliant).' },
  { id: 'aggressive',  label: 'Aggressive stewardship (–25%)',     consumptionDeltaPct: -25,
    description: 'Oxford-style fluoroquinolone restriction + carbapenem pre-authorisation; modelled on Dingle 2017 natural experiment.' },
  { id: 'crisis',      label: 'Crisis-driven cut (–40%)',          consumptionDeltaPct: -40,
    description: 'Wartime / supply-shock / surge scenario; extreme reduction with potential undertreatment risk.' },
];

/** Project resistance % over `years` horizon under a consumption-delta % vs
 *  baseline. Linear approximation; falls out of the ecological-elasticity
 *  literature which itself uses ~5-year horizons. */
function projectResistance(target: AmrTarget, years: number, consumptionDeltaPct: number, baselineDdd: number): number {
  // Convert % consumption delta into absolute DDD-shift
  const dddShift = baselineDdd * (consumptionDeltaPct / 100);
  const yearScale = years / 5;  // elasticity is 5-year; scale linearly
  const delta = target.elasticity * dddShift * yearScale * 100;  // back to pp
  return Math.max(0, Math.min(100, target.baselineResistancePct + delta));
}

export function AmrFeedbackLoop() {
  const [baselineDdd, setBaselineDdd] = useState<number>(20);  // EU-15 average ≈ 20 DDD/1000/day outpatient
  const [horizonYears, setHorizonYears] = useState<number>(5);

  const projections = useMemo(() => {
    return AMR_TARGETS.map(target => {
      const scenarioProjections = STEWARDSHIP_SCENARIOS.map(scenario => ({
        scenario,
        projectedResistancePct: projectResistance(target, horizonYears, scenario.consumptionDeltaPct, baselineDdd),
        deltaVsStatusQuo: 0,  // filled in below
      }));
      const statusQuo = scenarioProjections[0]!.projectedResistancePct;
      for (const sp of scenarioProjections) {
        sp.deltaVsStatusQuo = sp.projectedResistancePct - statusQuo;
      }
      return { target, scenarioProjections };
    });
  }, [baselineDdd, horizonYears]);

  const handlePin = (targetId: string, scenarioId: string, projectedPct: number) => {
    const target = AMR_TARGETS.find(t => t.pathogenId === targetId)!;
    const scenario = STEWARDSHIP_SCENARIOS.find(s => s.id === scenarioId)!;
    pinForBrief({
      id: `amr-${targetId}-${scenarioId}-${horizonYears}y-${baselineDdd}ddd`,
      surface: '§7.29',
      label: `${target.catalogLabel} · ${scenario.label} · ${projectedPct.toFixed(1)}% at ${horizonYears}y`,
      value: projectedPct,
      context: {
        pathogenId: targetId,
        scenarioId,
        baselineDdd,
        horizonYears,
        selectionDriver: target.selectionDriver,
        primarySource: target.primarySource,
        consumptionDriven: target.consumptionDriven,
      },
    });
  };

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.29</span>
        <h1 className="surface__title">AMR usage → emergence feedback loop</h1>
      </div>
      <div className="surface__sub">
        Antibiotic-consumption pressure × literature-anchored resistance elasticity ·
        {' '}5 priority AMR organisms · {horizonYears}-year forward projection
      </div>

      <div className="panel" style={{ background: 'var(--bm-warn-subtle, rgba(255,180,80,0.08))', borderColor: 'var(--bm-warn, #d4a857)' }}>
        <div className="panel__head"><span className="panel__title" style={{ color: 'var(--bm-warn, #d4a857)' }}>Source caveat — no live ESAC-Net / NARMS / GLASS feed</span></div>
        <div style={{ padding: '10px 14px', fontSize: 12.5, lineHeight: 1.55, color: 'var(--bm-text-muted)' }}>
          Consumption inputs are operator-tunable, NOT pulled from a live surveillance feed.
          Resistance elasticities are literature-anchored 5-year ecological estimates with
          per-organism citations (hover the source pill). When an ESAC-Net / NARMS / GLASS
          partnership lands (Phase 13+) the same surface consumes the live adapter; model + UI
          do not change. See the surface docblock for the partnership rationale.
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Consumption baseline + horizon</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Baseline antibiotic consumption · DDD per 1000 inhabitants per day</span>
            <input
              type="number" min="0" max="80" step="0.5"
              value={baselineDdd}
              onChange={e => setBaselineDdd(parseFloat(e.target.value) || 0)}
            />
            <span style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)', marginTop: 4 }}>
              Reference: EU-15 outpatient average ≈ 20 DDD/1000/day (ESAC-Net 2023);
              Greece + France &gt; 30; Netherlands + Estonia &lt; 12. ECDC ESAC-Net Atlas.
            </span>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Projection horizon · years</span>
            <select value={horizonYears} onChange={e => setHorizonYears(parseInt(e.target.value, 10))}>
              <option value="1">1 year</option>
              <option value="3">3 years</option>
              <option value="5">5 years (literature anchor)</option>
              <option value="10">10 years</option>
            </select>
          </label>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Per-organism projection · resistance % at {horizonYears}y</span></div>
        <table className="dtable">
          <thead>
            <tr>
              <th>Organism</th>
              <th>Selection driver</th>
              <th>Elasticity</th>
              <th className="num">Baseline %</th>
              {STEWARDSHIP_SCENARIOS.map(s => (
                <th key={s.id} className="num" title={s.description}>{s.label}</th>
              ))}
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {projections.map(({ target, scenarioProjections }) => (
              <tr key={target.pathogenId}>
                <td>
                  <strong>{target.catalogLabel}</strong>
                  {!target.consumptionDriven && (
                    <span style={{
                      display: 'inline-block', marginLeft: 6,
                      padding: '1px 5px', borderRadius: 3,
                      fontSize: 9.5, fontWeight: 700,
                      color: 'var(--bm-warn, #d4a857)',
                      background: 'var(--bm-warn-subtle, rgba(255,180,80,0.14))',
                      border: '1px solid var(--bm-warn, #d4a857)',
                    }} title="Resistance is dominantly transmission-driven, not consumption-driven — stewardship lever is weakly effective for this organism.">
                      transmission-driven
                    </span>
                  )}
                </td>
                <td style={{ fontSize: 11.5, color: 'var(--bm-text-muted)' }}>{target.selectionDriver}</td>
                <td className="num" title="Marginal resistance-prevalence change per +1 DDD/1000/day (5-year horizon).">
                  {target.elasticity.toFixed(3)}
                </td>
                <td className="num">{target.baselineResistancePct}%</td>
                {scenarioProjections.map(sp => {
                  const delta = sp.deltaVsStatusQuo;
                  const color = delta < -1 ? 'var(--bm-accent, #6ea3ff)'
                              : delta > 1 ? 'var(--bm-danger, #e56666)'
                              : 'var(--bm-text)';
                  return (
                    <td key={sp.scenario.id} className="num" style={{ color, fontWeight: 600 }}>
                      <button
                        type="button"
                        onClick={() => handlePin(target.pathogenId, sp.scenario.id, sp.projectedResistancePct)}
                        title={`Pin to brief · ${sp.scenario.label} → ${sp.projectedResistancePct.toFixed(1)}% at ${horizonYears}y`}
                        style={{
                          background: 'transparent', border: 'none', padding: 0,
                          color: 'inherit', font: 'inherit', cursor: 'pointer',
                        }}
                      >
                        {sp.projectedResistancePct.toFixed(1)}%
                        {sp.scenario.id !== 'status-quo' && (
                          <span style={{ fontSize: 9.5, marginLeft: 4, opacity: 0.7 }}>
                            ({delta > 0 ? '+' : ''}{delta.toFixed(1)})
                          </span>
                        )}
                      </button>
                    </td>
                  );
                })}
                <td style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>{target.primarySource}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Feedback-loop mechanism · how this surface plugs into decision support</span></div>
        <ol style={{ padding: '12px 14px 12px 32px', margin: 0, fontSize: 12.5, lineHeight: 1.6, color: 'var(--bm-text-muted)' }}>
          <li><strong>Cultural-risk register</strong> flags antimicrobial-prescribing-norm domains (over-prescription cultures / OTC sales / agricultural use) that elevate the consumption-baseline starting point.</li>
          <li><strong>§7.5 Intervention Planner</strong> stewardship-bundle NPI (Phase 11+ catalog addition) selects the consumption-delta % that drives this projection.</li>
          <li><strong>§7.19 Cost-per-DALY</strong> ingests the DALY-weighted resistance projection (10× treatment cost differential for resistant infections per WHO 2014 AMR-economics report).</li>
          <li><strong>§7.25 Minister Briefing Pack</strong> PHIA-tags the projected resistance change as "likely / highly likely" based on consumption-driven vs transmission-driven flag + elasticity confidence.</li>
          <li><strong>§7.21 After-Action Review</strong> replays the realised consumption + resistance trajectory vs the projection — calibration check on the elasticity assumptions.</li>
        </ol>
      </div>
    </div>
  );
}
